import { Moment } from 'moment';
import {
  GridColDef,
} from '@mui/x-data-grid';
import Typography from '@mui/material/Typography';
import startCase from 'lodash/startCase';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import moment from 'moment-timezone';
import { getDatesRange, getTimezone } from '@mgm/common/utils';
import { IntegrationExportTable } from '../../Shared/components';
import { useGetGustoExportAPI } from '../hooks/useGustoApi';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getGustoReportData } from '../../../../store/payroll/selectors';

const GustoExportTable = () => {
  useGetGustoExportAPI();
  const { t } = useTranslation(['inputs', 'payroll']);
  const { mutate, isLoading } = useGetGustoExportAPI();
  const getGustoReports = useAppSelector(getGustoReportData);
  const { fromDate, toDate } = getDatesRange();

  const [dates, setDates] = useState<[Moment, Moment]>([
    moment.tz(fromDate, getTimezone()),
    moment.tz(toDate, getTimezone()),
  ]);

  const columns: Array<GridColDef> = [
    {
      field: 'first_name',
      headerName: t('name'),
      minWidth: 130,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {startCase(row.first_name)}
          {' '}
          {startCase(row.last_name)}
        </Typography>
      ),
      flex: 1,
      editable: true,
    },
    {
      field: 'email',
      headerName: t('email'),
      minWidth: 200,
      flex: 1,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.email || '/'}
        </Typography>
      ),
    },
    {
      field: 'title',
      headerName: t('position'),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.title || '/'}
        </Typography>
      ),
    },
    {
      field: 'gusto_employee_id',
      headerName: t('gustoEmployeeId', { ns: 'payroll' }),
      minWidth: 200,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.gusto_employee_id || '/'}
        </Typography>
      ),
    },
    {
      field: 'ssn',
      headerName: 'SSN',
      minWidth: 80,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.ssn || 0}
        </Typography>
      ),
    },
    {
      field: 'reimbursement',
      headerName: t('reimbursement', { ns: 'payroll' }),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.reimbursement || 0}
        </Typography>
      ),
    },
    {
      field: 'regular_hours',
      headerName: t('regularHours', { ns: 'payroll' }),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.regular_hours || 0}
        </Typography>
      ),
    },
    {
      field: 'cash_tips',
      headerName: t('cashTips', { ns: 'payroll' }),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.cash_tips || 0}
        </Typography>
      ),
    },
    {
      field: 'bonus',
      headerName: t('bonus', { ns: 'payroll' }),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.bonus || 0}
        </Typography>
      ),
    },
    {
      field: 'commission',
      headerName: t('commission', { ns: 'payroll' }),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.commission || 0}
        </Typography>
      ),
    },
    {
      field: 'correction_payment',
      headerName: t('correctionPayment', { ns: 'payroll' }),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.correction_payment || 0}
        </Typography>
      ),
    },
    {
      field: 'double_overtime_hours',
      headerName: t('doubleOvertime', { ns: 'payroll' }),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.double_overtime_hours || 0}
        </Typography>
      ),
    },
    {
      field: 'paycheck_tips',
      headerName: t('paycheckTips', { ns: 'payroll' }),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.paycheck_tips || 0}
        </Typography>
      ),
    },
    {
      field: 'overtime_hours',
      headerName: t('overtimeHours', { ns: 'payroll' }),
      minWidth: 130,
      flex: 1,
      editable: true,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.overtime_hours || 0}
        </Typography>
      ),
    },
    {
      field: 'personal_note',
      headerName: t('personalNote', { ns: 'payroll' }),
      minWidth: 130,
      flex: 1,
      renderCell: ({ row }) => (
        <Typography variant="caption">
          {row.personal_note || '/'}
        </Typography>
      ),
      editable: true,
    },
  ];

  const getReport = (emails: Array<string>, dates: [Moment, Moment]) => {
    const data = {
      emails,
      dates,
    };

    mutate(data);
  };

  return (
    <IntegrationExportTable
      getReport={getReport}
      isLoading={isLoading}
      columns={columns}
      integrationName="Gusto"
      rowsData={getGustoReports}
      dates={dates}
      setDates={setDates}
    />
  );
};

export {
  GustoExportTable,
};
